// src/shared/ui/components/drop-zone.tsx
// The file drop target the patient flows share (the approved design's PaDropZone), as one adapter for the PA's drop
// zones (Import patients, the New file popup, the signed-consent upload; the intake inbox bar is the intake area's).
// Presentational: the caller keeps its own drag / drop / click handlers, its hidden <input type="file"> (pass it as
// `children`) and its file checks. Looks:
//   card — a dashed 12px box, the upload mark, the title and the limits line (Import patients, Upload a signed consent);
//   docs — the three-sheet stack, the title, the formats line and the caller's button (New file);
//   bar  — one row: brand mark, title + limits, the caller's button at the right.
// `DropFileRow` is the chosen-file row (file glyph, name, size, the caller's remove button).
// Look: showcase-patient.css `/* drop zone */`.
import type { HTMLAttributes, ReactNode } from 'react';
import { Icon } from '@scripsteam/scrips-signal-ds';

export type DropZoneLook = 'card' | 'docs' | 'bar';

export interface DropZoneProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  look?: DropZoneLook;
  /** Render the zone as a <label> (so a click opens the wrapped file input) or a <div>. */
  as?: 'label' | 'div';
  title: ReactNode;
  hint?: ReactNode;
  /** The caller's button (Browse / Choose files), after the text. */
  action?: ReactNode;
  dragging?: boolean;
  disabled?: boolean;
  /** The hidden file input and anything else the caller keeps inside the zone. */
  children?: ReactNode;
}

export function DropZone({
  look = 'card',
  as = 'div',
  title,
  hint,
  action,
  dragging = false,
  disabled = false,
  children,
  ...rest
}: DropZoneProps) {
  const Root = as;
  return (
    <Root
      data-slot="drop-zone"
      data-look={look}
      data-dragging={dragging ? 'true' : undefined}
      data-disabled={disabled ? 'true' : undefined}
      {...rest}
    >
      {look === 'docs' ? (
        <span data-slot="drop-zone-sheets" aria-hidden="true">
          <span data-slot="drop-zone-sheet" />
          <span data-slot="drop-zone-sheet" />
          <span data-slot="drop-zone-sheet">
            <Icon name="file" size={20} color="currentColor" aria-hidden />
          </span>
        </span>
      ) : (
        <span data-slot="drop-zone-mark" aria-hidden="true">
          <Icon name="upload" size={24} color="currentColor" aria-hidden />
        </span>
      )}
      <span data-slot="drop-zone-text">
        <span data-slot="drop-zone-title">{title}</span>
        {hint != null ? <span data-slot="drop-zone-hint">{hint}</span> : null}
      </span>
      {action}
      {children}
    </Root>
  );
}

export function DropFileRow({ name, size, action }: { name: ReactNode; size?: ReactNode; action?: ReactNode }) {
  return (
    <div data-slot="drop-file">
      <Icon name="file" size={16} color="currentColor" aria-hidden />
      <span data-slot="drop-file-text">
        <span data-slot="drop-file-name">{name}</span>
        {size != null ? <span data-slot="drop-file-size">{size}</span> : null}
      </span>
      {action}
    </div>
  );
}
